import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useNotifications } from '@revenuewell/uc-notifications-client';
import { ChannelUpdateEvent } from '@revenuewell/uc-notifications-client/dist/types/notifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCommsFilter } from '../../../../hooks/use-communications';
import { usePubnub } from '../../../../hooks/use-pubnub/use-pubnub';
import InboxService from '../../../../services/inbox-service/inbox-service';
import { ChannelSearch } from '../../../../services/inbox-service/types';
import { Status } from './communications-widget';

export interface ChannelStatus extends Status {
  channelId: string;
}

export const useCommunicationsStatus = (patientPhoneNumbers: string[]) => {
  const { updateCommsFilter } = useCommsFilter();
  const { oidcService } = useOidcWithAdmin();
  const { config } = useConfig();
  const { listen } = useNotifications();
  const { mailboxes } = usePubnub();
  const [channelsStatuses, setChannelsStatuses] = useState<ChannelStatus[]>([]);
  const [finalStatus, setFinalStatus] = useState<Status>({});
  const [patientMailboxIds, setPatientMailboxIds] = useState<number[]>([]);
  const inboxService = useMemo(() => InboxService.getInstance(config, oidcService), [config, oidcService]);

  const setUnread = (channels: ChannelSearch[]) => {
    channels.forEach((element) => {
      const phoneNumber = element.channelId.split('.')[1];
      inboxService.applyChannelAction(phoneNumber, element.mailboxId, { isUnread: false });
    });
  };

  const fetchStatuses = useCallback(
    async (phoneNumbers: string[]) => {
      if (!mailboxes.length || !inboxService || !phoneNumbers.length) return;

      const searchRequest = {
        mailboxIds: mailboxes.map((m) => m.id),
        phoneNumbers: phoneNumbers
      };

      const channels = await inboxService.channelSearch(searchRequest);

      if (channels.length) {
        setPatientMailboxIds(channels.map((channel) => channel.mailboxId));
        const newStatuses: ChannelStatus[] = channels.map((channel) => ({
          channelId: channel.channelId,
          isUnread: channel.isUnread,
          isArchived: channel.isArchived,
          isSaved: channel.isSaved
        }));
        setUnread(channels);
        setChannelsStatuses(newStatuses);
      }
    },
    [inboxService, mailboxes]
  );

  useEffect(() => {
    fetchStatuses(patientPhoneNumbers);
  }, [patientPhoneNumbers, fetchStatuses]);

  const updateStatuses = useCallback(
    (obj: any) => {
      const newStatus: ChannelStatus = {
        channelId: obj.channelId,
        isUnread: obj.isUnread === 'True',
        isArchived: obj.isArchived === 'True',
        isSaved: obj.isSaved === 'True'
      };

      const index = channelsStatuses.findIndex((item) => item.channelId === newStatus.channelId);

      if (index !== -1) {
        const updatedChannelStatuses = [...channelsStatuses];
        updatedChannelStatuses[index] = newStatus;
        setChannelsStatuses(updatedChannelStatuses);
      } else setChannelsStatuses([...channelsStatuses, newStatus]);
    },
    [channelsStatuses]
  );

  useEffect(() => {
    return listen('channel-update', (event) => {
      const { data } = event as ChannelUpdateEvent;

      if (patientPhoneNumbers.indexOf(data.phoneNo) < 0) return;

      updateStatuses(data);
    });
  }, [listen, patientPhoneNumbers, updateStatuses]);

  const handleFinalStatusUpdate = (status: Status) => {
    setFinalStatus(status);
  };

  useEffect(() => {
    handleFinalStatusUpdate({
      isUnread: channelsStatuses.some((item) => item.isUnread),
      isSaved: channelsStatuses.some((item) => item.isSaved),
      isArchived: channelsStatuses.some((item) => item.isArchived)
    });
  }, [channelsStatuses]);

  const handleDeleteCommType = () => {
    updateCommsFilter({ commType: undefined });
  };

  const handleDeleteDeliveryType = () => {
    updateCommsFilter({ deliveryType: undefined });
  };

  return {
    finalStatus,
    patientMailboxIds,
    channelsStatuses,
    handleDeleteCommType,
    handleDeleteDeliveryType,
    setUnread,
    fetchStatuses,
    updateStatuses,
    handleFinalStatusUpdate
  };
};
